// Render editor block JSON (Tiptap node shape) into top-level DOM block
// elements. The paginator then distributes these across pages.

function inline(node: any): DocumentFragment {
  const frag = document.createDocumentFragment();
  const content = node?.content ?? [];
  for (const child of content) {
    if (child.type === "text") {
      let el: Node = document.createTextNode(child.text ?? "");
      for (const mark of child.marks ?? []) {
        const wrap = document.createElement(
          mark.type === "bold" ? "strong" : mark.type === "italic" ? "em" : mark.type === "code" ? "code" : "span",
        );
        if (mark.type === "link" && mark.attrs?.href) {
          const a = document.createElement("a");
          a.href = mark.attrs.href;
          a.rel = "noopener";
          a.target = "_blank";
          a.appendChild(el);
          el = a;
          continue;
        }
        wrap.appendChild(el);
        el = wrap;
      }
      frag.appendChild(el);
    } else {
      frag.appendChild(inline(child));
    }
  }
  return frag;
}

function imageBlock(node: any): HTMLElement {
  const a = node.attrs ?? {};
  const figure = document.createElement("figure");
  figure.className = `img-align-${a.align ?? "center"}`;
  const img = document.createElement("img");
  img.src = a.src ?? "";
  img.alt = a.alt ?? "";
  img.loading = "lazy";
  if (a.width) {
    const w = typeof a.width === "number" ? `${a.width}px` : String(a.width);
    img.style.width = w.endsWith("%") || w.endsWith("px") ? w : `${w}px`;
  }
  if (a.align === "full") img.style.width = "100%";
  figure.appendChild(img);
  if (a.title || a.caption) {
    const cap = document.createElement("figcaption");
    cap.textContent = a.title ?? a.caption;
    figure.appendChild(cap);
  }
  return figure;
}

function listBlock(node: any, ordered: boolean): HTMLElement {
  const list = document.createElement(ordered ? "ol" : "ul");
  for (const item of node.content ?? []) {
    const li = document.createElement("li");
    for (const child of item.content ?? []) {
      if (child.type === "paragraph") li.appendChild(inline(child));
      else li.appendChild(renderBlock(child) ?? document.createTextNode(""));
    }
    list.appendChild(li);
  }
  return list;
}

export function renderBlock(node: any): HTMLElement | null {
  switch (node.type) {
    case "heading": {
      const lvl = Math.min(Math.max(node.attrs?.level ?? 2, 1), 4);
      const h = document.createElement(`h${lvl}`);
      h.appendChild(inline(node));
      return h;
    }
    case "paragraph": {
      const p = document.createElement("p");
      p.appendChild(inline(node));
      if (!p.textContent?.trim()) p.innerHTML = "&nbsp;";
      return p;
    }
    case "blockquote": {
      const bq = document.createElement("blockquote");
      for (const child of node.content ?? []) {
        const el = renderBlock(child);
        if (el) bq.appendChild(el);
      }
      return bq;
    }
    case "bulletList":
      return listBlock(node, false);
    case "orderedList":
      return listBlock(node, true);
    case "horizontalRule":
      return document.createElement("hr");
    case "image":
      return imageBlock(node);
    case "codeBlock": {
      const pre = document.createElement("pre");
      const code = document.createElement("code");
      code.textContent = (node.content ?? []).map((t: any) => t.text ?? "").join("");
      pre.appendChild(code);
      return pre;
    }
    default:
      return null;
  }
}

// Render a chapter into block elements, prefixed by a styled chapter opener.
export function renderChapter(title: string, indexLabel: string, blocks: any[]): HTMLElement[] {
  const opener = document.createElement("header");
  opener.className = "chapter-open";
  opener.innerHTML = `<span class="kicker">${indexLabel}</span>`;
  const h = document.createElement("h1");
  h.textContent = title;
  opener.appendChild(h);

  const out: HTMLElement[] = [opener];
  let first = true;
  for (const node of blocks) {
    const el = renderBlock(node);
    if (!el) continue;
    if (first && el.tagName === "P") {
      el.classList.add("drop"); // drop-cap the first paragraph
      first = false;
    }
    out.push(el);
  }
  return out;
}
